import React, { useRef, useMemo, Suspense } from "react";
import { useFrame } from "@react-three/fiber";
import { RigidBody, CapsuleCollider } from "@react-three/rapier";
import { RoundedBox } from "@react-three/drei";
import * as THREE from "three";

export const WALK_START_X = -14;
export const WALK_END_X = 14;
const ROBOT_Z = 1.9;

// Fan Creator palette
const PURPLE = { color: "#7c3aed", emissive: "#a855f7", emissiveIntensity: 0.18, metalness: 0.55, roughness: 0.35 };
const TRIM = { color: "#8fb8d8", emissive: "#7dd3fc", emissiveIntensity: 0.16, metalness: 0.85, roughness: 0.3 };
const DARK = { color: "#1a0b2e", metalness: 0.6, roughness: 0.4 };
const PINK_GLOW = { color: "#ec4899", emissive: "#ec4899", emissiveIntensity: 2.2 };

// Lightning bolt — the Fan Creator mark
const useBoltGeometry = () =>
    useMemo(() => {
        const s = new THREE.Shape();
        s.moveTo(0.18, 1.0);
        s.lineTo(-0.32, 0.12);
        s.lineTo(-0.02, 0.12);
        s.lineTo(-0.18, -1.0);
        s.lineTo(0.32, -0.05);
        s.lineTo(0.02, -0.05);
        s.closePath();
        const g = new THREE.ExtrudeGeometry(s, {
            depth: 0.12,
            bevelEnabled: true,
            bevelThickness: 0.03,
            bevelSize: 0.03,
            bevelSegments: 2,
        });
        g.center();
        return g;
    }, []);

// "VOLT" — a code-sculpted Fan Creator mascot: chunky TV-head, headphones,
// bolt antenna, sneaker feet. Fully articulated, hand-tuned walk cycle.
const Mascot = ({ swingRef, progressRef }) => {
    const inner = useRef();
    const head = useRef();
    const eyeL = useRef();
    const eyeR = useRef();
    const shoulderL = useRef();
    const shoulderR = useRef();
    const elbowR = useRef();
    const hipL = useRef();
    const hipR = useRef();
    const kneeL = useRef();
    const kneeR = useRef();
    const footL = useRef();
    const footR = useRef();
    const boltMat = useRef();
    const packBoltMat = useRef();

    const phase = useRef(0);
    const wave = useRef({ state: "none", t: 0, done: false });
    const boltGeo = useBoltGeometry();

    useFrame((state, delta) => {
        const s = swingRef.current;
        const p = progressRef ? progressRef.current : 0;
        const t = state.clock.elapsedTime;

        phase.current += delta * 9 * (0.15 + s);
        const ph = phase.current;
        const sw = s;

        // --- wave state machine (celebrates at the final pillar) ---
        const w = wave.current;
        if (p < 0.9) w.done = false;
        if (p >= 0.985 && !w.done && sw <= 0.05 && w.state === "none") {
            w.state = "raising";
            w.t = 0;
        }
        if (w.state === "raising") {
            w.t += delta;
            if (w.t > 0.5) { w.state = "waving"; w.t = 0; }
        } else if (w.state === "waving") {
            w.t += delta;
            if (w.t > 2.2) { w.state = "lowering"; w.t = 0; }
        } else if (w.state === "lowering") {
            w.t += delta;
            if (w.t > 0.5) { w.state = "none"; w.done = true; }
        }
        const waving = w.state !== "none";
        const raiseT =
            w.state === "raising" ? Math.min(1, w.t / 0.5)
            : w.state === "waving" ? 1
            : w.state === "lowering" ? Math.max(0, 1 - w.t / 0.5)
            : 0;

        // --- walk cycle ---
        if (hipL.current) hipL.current.rotation.x = Math.sin(ph) * 0.55 * sw;
        if (hipR.current) hipR.current.rotation.x = -Math.sin(ph) * 0.55 * sw;
        if (kneeL.current) kneeL.current.rotation.x = Math.max(0, -Math.sin(ph)) * 0.95 * sw;
        if (kneeR.current) kneeR.current.rotation.x = Math.max(0, Math.sin(ph)) * 0.95 * sw;
        if (footL.current) footL.current.rotation.x = Math.sin(ph - 0.5) * 0.28 * sw;
        if (footR.current) footR.current.rotation.x = -Math.sin(ph - 0.5) * 0.28 * sw;

        if (shoulderL.current) shoulderL.current.rotation.x = -Math.sin(ph) * 0.5 * sw + Math.sin(t * 1.4) * 0.04;
        if (shoulderR.current) {
            shoulderR.current.rotation.x = waving
                ? 0
                : Math.sin(ph) * 0.5 * sw - Math.sin(t * 1.4) * 0.04;
            // raise the right arm up beside the head
            shoulderR.current.rotation.z = THREE.MathUtils.lerp(
                shoulderR.current.rotation.z,
                2.35 * raiseT,
                0.35
            );
        }
        if (elbowR.current) {
            elbowR.current.rotation.z = waving && raiseT >= 1 ? Math.sin(t * 9) * 0.55 : 0;
            elbowR.current.rotation.x = waving ? 0 : -0.25 - sw * 0.2;
        }
        if (!waving && shoulderR.current.rotation.z !== 0 && w.done) {
            shoulderR.current.rotation.z = THREE.MathUtils.lerp(shoulderR.current.rotation.z, 0, 0.2);
        }

        if (inner.current) {
            inner.current.position.y = Math.abs(Math.sin(ph)) * 0.08 * sw + Math.sin(t * 2) * 0.015;
            inner.current.rotation.z = -0.035 * sw;
            // face the camera when idle / waving, face travel direction when walking
            const faceCam = waving ? 0.85 : 0.4 * (1 - sw);
            inner.current.rotation.y = THREE.MathUtils.lerp(
                inner.current.rotation.y,
                -faceCam,
                delta * 4
            );
        }
        if (head.current) {
            head.current.rotation.y = waving ? 0 : Math.sin(t * 0.8) * 0.22 * (1 - sw);
            head.current.rotation.x = Math.sin(ph * 2) * 0.04 * sw;
            head.current.rotation.z = waving ? 0.14 * raiseT : 0;
        }

        // blink
        const blink = t % 3.7 < 0.12 ? 0.12 : 1;
        if (eyeL.current) eyeL.current.scale.y = blink;
        if (eyeR.current) eyeR.current.scale.y = blink;

        // bolt pulse
        const pulse = 2.0 + Math.sin(t * 3) * 0.55;
        if (boltMat.current) boltMat.current.emissiveIntensity = pulse;
        if (packBoltMat.current) packBoltMat.current.emissiveIntensity = pulse;
    });

    return (
        <group ref={inner}>
            {/* pelvis */}
            <RoundedBox args={[0.52, 0.26, 0.4]} radius={0.08} position={[0, 1.02, 0]}>
                <meshStandardMaterial {...DARK} />
            </RoundedBox>
            {/* belt */}
            <mesh position={[0, 1.14, 0]} rotation={[Math.PI / 2, 0, 0]}>
                <torusGeometry args={[0.33, 0.045, 12, 32]} />
                <meshStandardMaterial {...TRIM} />
            </mesh>
            {/* torso */}
            <mesh position={[0, 1.52, 0]}>
                <capsuleGeometry args={[0.37, 0.42, 6, 16]} />
                <meshStandardMaterial {...PURPLE} />
            </mesh>
            {/* chest emblem: halo + bolt */}
            <mesh position={[0, 1.56, 0.4]}>
                <circleGeometry args={[0.21, 32]} />
                <meshStandardMaterial color="#1a0b2e" emissive="#a855f7" emissiveIntensity={0.7} metalness={0.6} roughness={0.35} />
            </mesh>
            <mesh geometry={boltGeo} position={[0, 1.56, 0.43]} scale={0.2}>
                <meshStandardMaterial ref={boltMat} {...PINK_GLOW} metalness={0.3} roughness={0.25} />
            </mesh>
            {/* backpack with bolt — faces the camera during the walk */}
            <RoundedBox args={[0.42, 0.5, 0.2]} radius={0.06} position={[0, 1.5, -0.42]}>
                <meshStandardMaterial {...DARK} />
            </RoundedBox>
            <mesh position={[0, 1.5, -0.53]} rotation={[0, Math.PI, 0]}>
                <circleGeometry args={[0.16, 32]} />
                <meshStandardMaterial color="#1a0b2e" emissive="#a855f7" emissiveIntensity={0.7} />
            </mesh>
            <mesh geometry={boltGeo} position={[0, 1.5, -0.56]} rotation={[0, Math.PI, 0]} scale={0.15}>
                <meshStandardMaterial ref={packBoltMat} {...PINK_GLOW} metalness={0.3} roughness={0.25} />
            </mesh>

            {/* neck */}
            <mesh position={[0, 1.94, 0]}>
                <cylinderGeometry args={[0.11, 0.13, 0.14, 12]} />
                <meshStandardMaterial {...TRIM} />
            </mesh>

            {/* head */}
            <group ref={head} position={[0, 2.32, 0]}>
                <RoundedBox args={[0.82, 0.66, 0.72]} radius={0.16} smoothness={4}>
                    <meshStandardMaterial {...PURPLE} />
                </RoundedBox>
                {/* visor face */}
                <RoundedBox args={[0.6, 0.34, 0.08]} radius={0.04} position={[0, 0.02, 0.35]}>
                    <meshStandardMaterial color="#0d0518" metalness={0.5} roughness={0.25} emissive="#2a0a1e" emissiveIntensity={0.4} />
                </RoundedBox>
                {/* eyes */}
                <mesh ref={eyeL} position={[-0.15, 0.04, 0.4]}>
                    <capsuleGeometry args={[0.045, 0.09, 4, 10]} />
                    <meshStandardMaterial {...PINK_GLOW} />
                </mesh>
                <mesh ref={eyeR} position={[0.15, 0.04, 0.4]}>
                    <capsuleGeometry args={[0.045, 0.09, 4, 10]} />
                    <meshStandardMaterial {...PINK_GLOW} />
                </mesh>
                {/* headphones */}
                <mesh position={[-0.46, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
                    <cylinderGeometry args={[0.17, 0.17, 0.1, 20]} />
                    <meshStandardMaterial {...TRIM} />
                </mesh>
                <mesh position={[0.46, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
                    <cylinderGeometry args={[0.17, 0.17, 0.1, 20]} />
                    <meshStandardMaterial {...TRIM} />
                </mesh>
                <mesh position={[-0.52, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
                    <cylinderGeometry args={[0.08, 0.08, 0.03, 14]} />
                    <meshStandardMaterial {...PINK_GLOW} emissiveIntensity={1.4} />
                </mesh>
                <mesh position={[0.52, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
                    <cylinderGeometry args={[0.08, 0.08, 0.03, 14]} />
                    <meshStandardMaterial {...PINK_GLOW} emissiveIntensity={1.4} />
                </mesh>
                {/* headband */}
                <mesh position={[0, 0.1, 0]} scale={[1, 0.72, 1]}>
                    <torusGeometry args={[0.38, 0.04, 10, 32, Math.PI]} />
                    <meshStandardMaterial {...TRIM} />
                </mesh>
                {/* bolt antenna */}
                <mesh position={[0, 0.45, 0]}>
                    <cylinderGeometry args={[0.02, 0.02, 0.2, 8]} />
                    <meshStandardMaterial {...TRIM} />
                </mesh>
                <mesh geometry={boltGeo} position={[0, 0.68, 0]} scale={0.14}>
                    <meshStandardMaterial {...PINK_GLOW} emissiveIntensity={2.6} />
                </mesh>
            </group>

            {/* left arm (local -x) */}
            <group ref={shoulderL} position={[-0.5, 1.72, 0]}>
                <mesh>
                    <sphereGeometry args={[0.12, 14, 14]} />
                    <meshStandardMaterial {...TRIM} />
                </mesh>
                <mesh position={[0, -0.2, 0]}>
                    <capsuleGeometry args={[0.09, 0.26, 4, 10]} />
                    <meshStandardMaterial {...PURPLE} />
                </mesh>
                <mesh position={[0, -0.4, 0]}>
                    <sphereGeometry args={[0.09, 12, 12]} />
                    <meshStandardMaterial {...TRIM} />
                </mesh>
                <mesh position={[0, -0.56, 0]}>
                    <capsuleGeometry args={[0.08, 0.22, 4, 10]} />
                    <meshStandardMaterial {...PURPLE} />
                </mesh>
                <mesh position={[0, -0.76, 0]}>
                    <sphereGeometry args={[0.12, 12, 12]} />
                    <meshStandardMaterial {...TRIM} />
                </mesh>
            </group>
            {/* right arm (local +x) — the waving arm */}
            <group ref={shoulderR} position={[0.5, 1.72, 0]}>
                <mesh>
                    <sphereGeometry args={[0.12, 14, 14]} />
                    <meshStandardMaterial {...TRIM} />
                </mesh>
                <mesh position={[0, -0.2, 0]}>
                    <capsuleGeometry args={[0.09, 0.26, 4, 10]} />
                    <meshStandardMaterial {...PURPLE} />
                </mesh>
                <group ref={elbowR} position={[0, -0.4, 0]}>
                    <mesh>
                        <sphereGeometry args={[0.09, 12, 12]} />
                        <meshStandardMaterial {...TRIM} />
                    </mesh>
                    <mesh position={[0, -0.16, 0]}>
                        <capsuleGeometry args={[0.08, 0.22, 4, 10]} />
                        <meshStandardMaterial {...PURPLE} />
                    </mesh>
                    <mesh position={[0, -0.36, 0]}>
                        <sphereGeometry args={[0.12, 12, 12]} />
                        <meshStandardMaterial {...TRIM} />
                    </mesh>
                </group>
            </group>

            {/* left leg */}
            <group ref={hipL} position={[-0.18, 0.98, 0]}>
                <mesh position={[0, -0.16, 0]}>
                    <capsuleGeometry args={[0.11, 0.26, 4, 10]} />
                    <meshStandardMaterial {...PURPLE} />
                </mesh>
                <group ref={kneeL} position={[0, -0.36, 0]}>
                    <mesh>
                        <sphereGeometry args={[0.1, 12, 12]} />
                        <meshStandardMaterial {...TRIM} />
                    </mesh>
                    <mesh position={[0, -0.2, 0]}>
                        <capsuleGeometry args={[0.09, 0.24, 4, 10]} />
                        <meshStandardMaterial {...PURPLE} />
                    </mesh>
                    {/* chunky sneaker */}
                    <group ref={footL} position={[0, -0.44, 0]}>
                        <RoundedBox args={[0.26, 0.15, 0.46]} radius={0.06} position={[0, -0.06, 0.08]}>
                            <meshStandardMaterial {...TRIM} />
                        </RoundedBox>
                        <RoundedBox args={[0.28, 0.06, 0.5]} radius={0.03} position={[0, -0.14, 0.08]}>
                            <meshStandardMaterial color="#ec4899" emissive="#ec4899" emissiveIntensity={0.9} />
                        </RoundedBox>
                    </group>
                </group>
            </group>
            {/* right leg */}
            <group ref={hipR} position={[0.18, 0.98, 0]}>
                <mesh position={[0, -0.16, 0]}>
                    <capsuleGeometry args={[0.11, 0.26, 4, 10]} />
                    <meshStandardMaterial {...PURPLE} />
                </mesh>
                <group ref={kneeR} position={[0, -0.36, 0]}>
                    <mesh>
                        <sphereGeometry args={[0.1, 12, 12]} />
                        <meshStandardMaterial {...TRIM} />
                    </mesh>
                    <mesh position={[0, -0.2, 0]}>
                        <capsuleGeometry args={[0.09, 0.24, 4, 10]} />
                        <meshStandardMaterial {...PURPLE} />
                    </mesh>
                    <group ref={footR} position={[0, -0.44, 0]}>
                        <RoundedBox args={[0.26, 0.15, 0.46]} radius={0.06} position={[0, -0.06, 0.08]}>
                            <meshStandardMaterial {...TRIM} />
                        </RoundedBox>
                        <RoundedBox args={[0.28, 0.06, 0.5]} radius={0.03} position={[0, -0.14, 0.08]}>
                            <meshStandardMaterial color="#ec4899" emissive="#ec4899" emissiveIntensity={0.9} />
                        </RoundedBox>
                    </group>
                </group>
            </group>
        </group>
    );
};

const Robot = ({ progressRef }) => {
    const body = useRef();
    const swing = useRef(0);
    const prevX = useRef(WALK_START_X);

    useFrame((_, delta) => {
        if (!body.current) return;
        const p = THREE.MathUtils.clamp(progressRef.current, 0, 1);
        const targetX = THREE.MathUtils.lerp(WALK_START_X, WALK_END_X, p);
        const cur = body.current.translation();
        const nx = cur.x + (targetX - cur.x) * Math.min(1, delta * 5);
        body.current.setNextKinematicTranslation({ x: nx, y: 0, z: ROBOT_Z });

        const vel = Math.abs(nx - prevX.current) / Math.max(delta, 1e-4);
        prevX.current = nx;
        const targetSwing = THREE.MathUtils.clamp(vel / 2.5, 0, 1);
        swing.current = THREE.MathUtils.lerp(swing.current, targetSwing, delta * 6);
    });

    return (
        <RigidBody
            ref={body}
            type="kinematicPosition"
            colliders={false}
            position={[WALK_START_X, 0, ROBOT_Z]}
            enabledRotations={[false, false, false]}
        >
            <CapsuleCollider args={[0.55, 0.7]} position={[0, 1.35, 0]} />
            <Suspense fallback={null}>
                <group rotation={[0, Math.PI / 2, 0]}>
                    <Mascot swingRef={swing} progressRef={progressRef} />
                </group>
            </Suspense>
        </RigidBody>
    );
};

export default Robot;
